import type { ChangeEvent } from "react";
import React, { useMemo, useRef } from "react";
import type { DealerResultType } from "../../../../../../common-deprecated/utils/dealerConstants";
import CarDeliveryDealerResult from "./CarDeliveryDealerResult";
import { CarDeliveryDestination, createFullAddress } from "../../../../utils/carDeliveryUtils";
import useDeliveryConfiguratorMap from "../../../../hooks/useDeliveryConfiguratorMap";
import { setCarDeliveryDestination } from "../../../../redux/actions/UscCarDeliveryActions";
import CarDeliveryAddressResult from "./CarDeliveryAddressResult";
import type { FormattedMapboxResultType } from "../../../../hooks/useCarDeliveryConfigurator";
import type { UscDispatchType } from "../../../../redux/store";
import { useUscSelector } from "../../../../redux/store";
import { UscLabel, useUscLabel } from "../../../../utils/labels";
import { trackCarDeliverySelectDeliveryType } from "../../../../utils/tracking";
import { useDispatch } from "../../../../../../shared-logic/redux/store";
import Preloader from "../../../../../shared-components/toyota/styles/molecules/Preloader";
import Button from "../../../../../shared-components/toyota/styles/atoms/Button";
import Text from "../../../../../shared-components/toyota/styles/atoms/Text";
import Icon from "../../../../../shared-components/toyota/styles/atoms/Icon";
import * as Styles from "./UscCarDeliverySelectDestination.styles";

type UscCarDeliverySelectDestinationProps = {
    onClear: () => void;
    onSearch: (event: ChangeEvent<HTMLInputElement>) => void;
    searchQuery: string;
    dealerResults: DealerResultType[];
    mapboxResults: FormattedMapboxResultType[];
    loading: boolean;
    errorLabel: string | null;
    dealerLocationClick: (dealer: DealerResultType) => void;
    mapboxLocationClick: (mapboxResult: FormattedMapboxResultType) => void;
};

// Mapbox integration
const DeliveryConfiguratorMap = (): JSX.Element | null => {
    const mapContainer = useRef<HTMLDivElement>(null);
    const { mapInitialized } = useDeliveryConfiguratorMap(mapContainer);

    return useMemo(
        () => (
            <>
                <Styles.Map>
                    <div ref={mapContainer} />
                </Styles.Map>
                <Styles.Loader>{!mapInitialized && <Preloader />}</Styles.Loader>
            </>
        ),
        [mapInitialized],
    );
};
const UscCarDeliverySelectDestination = (props: UscCarDeliverySelectDestinationProps): JSX.Element => {
    const dispatch = useDispatch<UscDispatchType>();

    const { searchQuery, dealerResults, mapboxResults, loading, errorLabel } = props;
    const { onClear, onSearch, dealerLocationClick, mapboxLocationClick } = props;

    const deliveryDestination = useUscSelector((state) => state.carDelivery.deliveryDestination);

    const [dealerInputPlaceholder, homeInputPlaceholder] = useUscLabel([
        "uscCarDeliveryInputDealerPlaceholder",
        "uscCarDeliveryInputHomePlaceholder",
    ]);
    const selectedDealer = useUscSelector(
        (state) => state.carDelivery.tempDeliveryConfig || state.carDelivery.deliveryConfig,
    );
    const uscEnv = useUscSelector((state) => state.vehicle.type);

    let placeholderText =
        deliveryDestination === CarDeliveryDestination.Dealer ? dealerInputPlaceholder : homeInputPlaceholder;
    if (selectedDealer?.address)
        placeholderText = selectedDealer.dealerName || createFullAddress(selectedDealer.address);

    return (
        <>
            <Styles.Header>
                <Text variant="h5-regular" tag="h3">
                    <UscLabel label="uscCarDeliverySelectDestinationTitle" />
                </Text>
                <Text tag="p">
                    <UscLabel label="uscCarDeliverySelectDestinationDescription" />
                </Text>
            </Styles.Header>
            <Styles.Toggle>
                <Styles.ToggleButton
                    type="button"
                    aria-pressed={deliveryDestination === CarDeliveryDestination.Dealer}
                    onClick={() => {
                        trackCarDeliverySelectDeliveryType(uscEnv, CarDeliveryDestination.Dealer);
                        dispatch(setCarDeliveryDestination(CarDeliveryDestination.Dealer));
                    }}
                >
                    <Styles.ButtonTitle>
                        <UscLabel label="uscCarDeliverySelectDestinationOptionDealership" />
                    </Styles.ButtonTitle>
                </Styles.ToggleButton>
                <Styles.ToggleButton
                    type="button"
                    aria-pressed={deliveryDestination === CarDeliveryDestination.Home}
                    onClick={() => {
                        trackCarDeliverySelectDeliveryType(uscEnv, CarDeliveryDestination.Home);
                        dispatch(setCarDeliveryDestination(CarDeliveryDestination.Home));
                    }}
                >
                    <Styles.ButtonTitle>
                        <UscLabel label="uscCarDeliverySelectDestinationOptionHome" />
                    </Styles.ButtonTitle>
                </Styles.ToggleButton>
            </Styles.Toggle>

            <Styles.MapWrapper>
                <DeliveryConfiguratorMap />
                <Styles.SelectWrapper>
                    <Styles.InputWrapper open={!!searchQuery || !!errorLabel}>
                        <input type="text" onChange={onSearch} value={searchQuery} placeholder={placeholderText} />
                        <Styles.InputAppend>
                            {loading && <Preloader size={26} />}
                            {!loading && (selectedDealer || searchQuery) && (
                                <Button variant="clean" onClick={onClear}>
                                    <Icon variant="close" />
                                </Button>
                            )}
                            <Button variant="clean">
                                <Icon variant="search" />
                            </Button>
                        </Styles.InputAppend>
                    </Styles.InputWrapper>
                    <Styles.CarLocationResults>
                        <ul role="listbox">
                            {!loading &&
                                deliveryDestination === CarDeliveryDestination.Dealer &&
                                dealerResults.length > 0 &&
                                dealerResults.map((dealer) => (
                                    <CarDeliveryDealerResult
                                        name={dealer.name}
                                        address={`${dealer.address.line1} ${dealer.address.line2} ${
                                            dealer.address.country || dealer.address.city
                                        }`}
                                        distance={dealer.distance}
                                        selectLocation={() => dealerLocationClick(dealer)}
                                    />
                                ))}
                            {!loading &&
                                deliveryDestination === CarDeliveryDestination.Home &&
                                mapboxResults.length > 0 &&
                                mapboxResults.map((mapboxResult) => (
                                    <CarDeliveryAddressResult
                                        address={mapboxResult.displayAddress}
                                        selectLocation={() => mapboxLocationClick(mapboxResult)}
                                    />
                                ))}
                            {!!errorLabel && <Styles.NoResultsErrorMessage>{errorLabel}</Styles.NoResultsErrorMessage>}
                        </ul>
                    </Styles.CarLocationResults>
                </Styles.SelectWrapper>
            </Styles.MapWrapper>
        </>
    );
};

export default UscCarDeliverySelectDestination;
